import { Building2, CheckCircle2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { parseWholeNumber } from '../lib/numeric';
import { Button, Field, Input, Textarea } from '../ui';

const digits = (s: string) => s.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '');
const emailOk = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);

interface Errors {
  societyName?: string;
  city?: string;
  adminName?: string;
  phone?: string;
  email?: string;
  flats?: string;
}

/** Public "register your society" page. It sends a request for the operator to review; nothing is created yet. */
export function Signup() {
  const [societyName, setSocietyName] = useState('');
  const [city, setCity] = useState('');
  const [adminName, setAdminName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [flats, setFlats] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot: hidden from people, filled in by bots
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  function validate(): Errors {
    const e: Errors = {};
    if (societyName.trim().length < 3) e.societyName = 'Enter your society\'s name';
    if (city.trim().length < 2) e.city = 'Enter the city';
    if (adminName.trim().length < 2) e.adminName = 'Enter your name';
    if (!/^[6-9]\d{9}$/.test(digits(phone))) e.phone = 'Enter a valid 10-digit mobile number';
    if (email.trim() && !emailOk(email.trim())) e.email = 'That email does not look right';
    if (flats.trim() && parseWholeNumber(flats, 1, 5000) === null) e.flats = 'Enter a whole number between 1 and 5000';
    return e;
  }

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    setFormError(null);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const flatsNumber = flats.trim() ? parseWholeNumber(flats, 1, 5000) : null;
      await api('/signup', {
        anon: true,
        body: {
          societyName: societyName.trim(),
          city: city.trim(),
          adminName: adminName.trim(),
          adminPhone: digits(phone),
          ...(email.trim() && { adminEmail: email.trim() }),
          ...(flatsNumber !== null && { flatsEstimate: flatsNumber }),
          ...(message.trim() && { message: message.trim() }),
          ...(website && { website }),
        },
      });
      setDone(digits(phone));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') setFormError('Too many requests from this network. Please try again in an hour, or call us.');
      else setFormError(err instanceof Error ? err.message : 'Could not send your request. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center bg-gradient-to-b from-brand-700 to-violet-700 px-5 py-8" style={{ paddingTop: 'max(2rem, var(--safe-top))', paddingBottom: 'max(2rem, var(--safe-bottom))' }}>
      <div className="w-full max-w-md">
        <div className="mb-6 text-center text-white">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
            <Building2 className="h-8 w-8" aria-hidden />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Register your society</h1>
          <p className="mt-1 text-white/80">Gate, community and helpdesk for your apartment</p>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-xl">
          {done ? (
            <div className="space-y-4 text-center" role="status">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" aria-hidden />
              <h2 className="text-xl font-bold">Request received</h2>
              <p className="text-slate-700">
                Thank you. We will contact you on <b>+91 {done}</b> (WhatsApp or call) within one working day to set up your society.
              </p>
              <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Once it is set up you will receive a one-time code. Open this site, enter the same mobile number and that code to sign in and start adding your flats and residents.
              </p>
              <Link to="/" className="block pt-1 font-semibold text-brand-700 underline">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <Field label="Society / apartment name" error={errors.societyName}>
                <Input value={societyName} onChange={(e) => setSocietyName(e.target.value)} maxLength={120} autoComplete="organization" autoFocus placeholder="e.g. Park Square" />
              </Field>
              <Field label="City" error={errors.city}>
                <Input value={city} onChange={(e) => setCity(e.target.value)} maxLength={60} autoComplete="address-level2" placeholder="e.g. Lucknow" />
              </Field>
              <Field label="Your name" hint="You will be the society's first admin" error={errors.adminName}>
                <Input value={adminName} onChange={(e) => setAdminName(e.target.value)} maxLength={80} autoComplete="name" />
              </Field>
              <Field label="Your mobile number" hint="You will sign in with this number" error={errors.phone}>
                <Input inputMode="numeric" autoComplete="tel-national" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={16} placeholder="98765 43210" />
              </Field>
              <Field label="Email (optional)" error={errors.email}>
                <Input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} />
              </Field>
              <Field label="Approximate number of flats (optional)" error={errors.flats}>
                <Input inputMode="numeric" pattern="[0-9]*" value={flats} onChange={(e) => setFlats(e.target.value)} maxLength={4} placeholder="e.g. 24" />
              </Field>
              <Field label="Anything we should know? (optional)">
                <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} />
              </Field>

              {/* Honeypot. Invisible and skipped by keyboard and screen readers; only automated form-fillers touch it. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>
                  Website
                  <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
                </label>
              </div>

              {formError && <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-800">{formError}</p>}
              <Button type="submit" size="lg" className="w-full" loading={busy}>Send request</Button>
              <p className="text-xs text-slate-500">We use these details only to set up your society and to contact you about it.</p>
              <p className="text-center text-sm text-slate-700">
                Already registered? <Link to="/" className="font-semibold text-brand-700 underline">Sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
